<template>
  <div class="flex flex-col h-full gap-5 overflow-y-auto">
    <div class="flex flex-col gap-1.5">
      <label class="text-sm font-medium">תווית (אופציונלי)</label>
      <InputText
        v-model="label"
        placeholder="לדוגמה: שעות נוספות לחגים"
        class="w-full" />
    </div>

    <div class="flex flex-col gap-2 min-w-[2rem]">
      <div class="flex items-center justify-between">
        <label class="text-sm font-medium">טווח תאריכים</label>
        <span v-if="rangeSummary" class="text-xs text-primary-700 font-medium">{{ rangeSummary }}</span>
      </div>
      <div class="border rounded-lg p-3 bg-white">
        <FullCalendar
          v-model="dateRange"
          selection-mode="range"
          mode="compact"
          :min-date="minDate"
          :primary-color="primaryColor"
          primary-font-color="#ffffff"
          items-bg="#ffffff"
          class="w-full" />
      </div>
      <span v-if="!rangeStart" class="text-xs text-gray-500">בחרו תאריך התחלה ותאריך סיום</span>
      <span v-else-if="!rangeEnd" class="text-xs text-gray-500">בחרו תאריך סיום</span>
    </div>

    <div v-if="branchOptions.length > 1" class="flex flex-col gap-1.5">
      <label class="text-sm font-medium">סניף</label>
      <Select
        v-model="branchId"
        :options="branchOptions"
        option-label="label"
        option-value="value"
        class="w-full"
        placeholder="בחרו סניף" />
    </div>

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium">תקף בימים <span class="text-gray-500 font-normal">(ברירת מחדל: כל הימים)</span></label>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="day in allDays"
          :key="day.value"
          type="button"
          :class="[
            'w-10 h-10 rounded-full border text-sm font-medium transition',
            daysOfWeek.includes(day.value)
              ? 'bg-primary text-white border-primary'
              : 'bg-white text-gray-700 border-gray-300 hover:border-primary'
          ]"
          @click="toggleDay(day.value)"
        >{{ day.short }}</button>
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <div class="flex items-center justify-between">
        <label class="text-sm font-medium">שעות התוספת</label>
        <Button
          size="small"
          icon="pi pi-plus"
          label="טווח נוסף"
          severity="secondary"
          text
          @click="addHoursRow" />
      </div>
      <div
        v-for="(row, idx) in hoursRows"
        :key="idx"
        class="flex items-center gap-2"
      >
        <Select
          v-model="row.from"
          :options="hours"
          option-label="name"
          option-value="value"
          class="flex-1"
          placeholder="מ-" />
        <span class="text-gray-400">—</span>
        <Select
          v-model="row.to"
          :options="hours"
          option-label="name"
          option-value="value"
          class="flex-1"
          placeholder="עד" />
        <button
          v-if="hoursRows.length > 1"
          type="button"
          class="text-red-500 p-2"
          @click="removeHoursRow(idx)"
        ><i class="pi pi-trash" /></button>
      </div>
    </div>

    <div v-if="errorMessage" class="text-red-500 text-sm">{{ errorMessage }}</div>

    <drawer-footer
      class="mt-auto"
      :approve-label="mode === 'edit' ? 'שמירת שינויים' : 'הוספה'"
      :approveDisable="loading"
      :approveLoading="loading"
      @approve-click="save"
      @cancel-click="$emit('close')"
    />
  </div>
</template>

<script setup lang="ts">
import type { IAvailabilityOverride, IAvailabilityOverrideInput } from '@torim-app/types';
import { AvailabilityOverrideTypeEnum, Days, hours, MAIN_BRANCH_ID, weekDaysInHebrew } from '@torim-app/types';

import { computed, ref } from 'vue';
import { DateTime } from 'luxon';
import { useToast } from 'vue-toastification';
import { FullCalendar } from '@torim-app/shared-components';
import Select from 'primevue/select';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import { useAdminAvailabilityStore, useAdminStore } from '@app/stores';

const props = defineProps<{
  providerId: string;
  override?: IAvailabilityOverride;
  mode: 'create' | 'edit';
}>();

const emit = defineEmits(['close']);

const adminAvailabilityStore = useAdminAvailabilityStore();
const adminStore = useAdminStore();
const toast = useToast();

// Branches the override can be tagged to: the ones this provider actually works
// at (main + any tagged). Single-branch providers fall back to just the main
// branch, in which case the picker stays hidden.
const branchOptions = computed<Array<{ label: string; value: string }>>(() => {
  if (!adminStore.hasBranches) return [];
  const providerBranchIds = adminAvailabilityStore.getProviderBranchIds(props.providerId);
  return adminStore.getBranchSelectOptions
    .filter(b => providerBranchIds.includes(b.id))
    .map(b => ({ label: b.name, value: b.id }));
});

const defaultBranchId = (): string | undefined => {
  if (!adminStore.hasBranches) return undefined;
  const opts = branchOptions.value;
  return opts.find(o => o.value === MAIN_BRANCH_ID)?.value ?? opts[0]?.value;
};

const branchId = ref<string | undefined>(props.override?.branchId ?? defaultBranchId());

const readPrimaryColor = (): string => {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--primary')?.trim();
  if (!raw) return '#8864ee';
  // PrimeVue theme stores --primary as a space-separated RGB triplet (e.g. "136 100 238")
  const parts = raw.split(/\s+/).map(Number);
  if (parts.length === 3 && parts.every(n => Number.isFinite(n))) {
    return `rgb(${parts[0]}, ${parts[1]}, ${parts[2]})`;
  }
  return raw.startsWith('#') || raw.startsWith('rgb') ? raw : '#8864ee';
};
const primaryColor = readPrimaryColor();

const allDays: Array<{ value: Days; short: string; full: string }> = [
  { value: Days.SUNDAY, short: 'א', full: weekDaysInHebrew[Days.SUNDAY] },
  { value: Days.MONDAY, short: 'ב', full: weekDaysInHebrew[Days.MONDAY] },
  { value: Days.TUESDAY, short: 'ג', full: weekDaysInHebrew[Days.TUESDAY] },
  { value: Days.WEDNESDAY, short: 'ד', full: weekDaysInHebrew[Days.WEDNESDAY] },
  { value: Days.THURSDAY, short: 'ה', full: weekDaysInHebrew[Days.THURSDAY] },
  { value: Days.FRIDAY, short: 'ו', full: weekDaysInHebrew[Days.FRIDAY] },
  { value: Days.SATURDAY, short: 'ש', full: weekDaysInHebrew[Days.SATURDAY] },
];

const minDate = new Date();

const initialRange = (): [Date | null, Date | null] => {
  if (props.override) {
    return [
      DateTime.fromISO(props.override.startDate).toJSDate(),
      DateTime.fromISO(props.override.endDate).toJSDate(),
    ];
  }
  return [null, null];
};

const initialHours = () => {
  if (props.override && props.override.hours.length > 0) {
    return props.override.hours.map(h => ({
      from: typeof h.from === 'string' ? h.from : h.from.name,
      to: typeof h.to === 'string' ? h.to : h.to.name,
    }));
  }
  return [{ from: '17:00', to: '20:00' }];
};

const label = ref<string>(props.override?.label ?? '');
const dateRange = ref<[Date | null, Date | null]>(initialRange());
const daysOfWeek = ref<Days[]>([...(props.override?.daysOfWeek ?? [])]);
const hoursRows = ref(initialHours());
const errorMessage = ref<string>('');
const loading = ref<boolean>(false);

const rangeStart = computed(() => dateRange.value[0]);
const rangeEnd = computed(() => dateRange.value[1]);

const rangeSummary = computed(() => {
  if (!rangeStart.value) return '';
  const s = DateTime.fromJSDate(rangeStart.value).setLocale('he').toFormat('dd/LL/yyyy');
  if (!rangeEnd.value) return s;
  const e = DateTime.fromJSDate(rangeEnd.value).setLocale('he').toFormat('dd/LL/yyyy');
  return s === e ? s : `${s} – ${e}`;
});

const toggleDay = (day: Days) => {
  if (daysOfWeek.value.includes(day)) {
    daysOfWeek.value = daysOfWeek.value.filter(d => d !== day);
  } else {
    daysOfWeek.value.push(day);
  }
};

const addHoursRow = () => {
  const last = hoursRows.value[hoursRows.value.length - 1];
  hoursRows.value.push({ from: last?.to ?? '09:00', to: '18:00' });
};

const removeHoursRow = (idx: number) => {
  hoursRows.value.splice(idx, 1);
};

const validate = (): IAvailabilityOverrideInput | null => {
  errorMessage.value = '';
  if (!rangeStart.value || !rangeEnd.value) {
    errorMessage.value = 'יש לבחור טווח תאריכים שלם';
    return null;
  }
  for (const row of hoursRows.value) {
    if (!row.from || !row.to || row.from >= row.to) {
      errorMessage.value = 'כל טווח שעות חייב להיות תקין (התחלה לפני סיום)';
      return null;
    }
  }

  return {
    providingClientId: props.providerId,
    type: AvailabilityOverrideTypeEnum.ADD,
    startDate: DateTime.fromJSDate(rangeStart.value).toFormat('yyyy-MM-dd'),
    endDate: DateTime.fromJSDate(rangeEnd.value).toFormat('yyyy-MM-dd'),
    daysOfWeek: daysOfWeek.value.length ? [...daysOfWeek.value] : undefined,
    hours: hoursRows.value.map(r => ({ from: r.from, to: r.to })),
    branchId: branchId.value,
    label: label.value.trim() || undefined,
  };
};

const save = async () => {
  const payload = validate();
  if (!payload) return;
  loading.value = true;
  try {
    if (props.mode === 'edit' && props.override?._id) {
      await adminAvailabilityStore.updateAvailabilityOverride(props.override._id, payload);
      toast.success('התוספת עודכנה');
    } else {
      await adminAvailabilityStore.createAvailabilityOverride(payload);
      toast.success('התוספת נוספה');
    }
    emit('close');
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לשמור את התוספת');
  } finally {
    loading.value = false;
  }
};
</script>
